// import { useAtom } from '@reatom/npm-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Iterable } from '@blade/std-lib'
import { Button, ResetIcon, Input } from '@blade/ui-kit'
import { FilterButton, FilterPopover } from '@root/shared/ui-kit'

import type { TrackingWallet } from '@root/entities/wallet-tracking'
import type { TrackingWalletsTableColumnId } from '@root/features/wallet-tracker/atoms/reatom-tracking-wallets-table'
import type { ColumnHeaderProps } from '@root/shared/ui-kit'
import type {
  CustomFilter,
  TableFilter,
} from '@root/shared/ui-kit/data-table/atoms/table-filter'

export type TrackingWalletsTableTagsFilterProps = ColumnHeaderProps<
  TrackingWallet.TrackingWallet,
  TrackingWalletsTableColumnId
>

export const TrackingWalletsTableTagsFilter = ({
  filter,
  setColumnFilter,
  clearColumnFilter,
  columnId,
}: TrackingWalletsTableTagsFilterProps) => {
  if (filter && filter.type !== 'custom') {
    throw new Error(
      'TrackingWalletsTableTagsFilter: filter must be a custom filter',
    )
  }
  const resolvedFilter = filter as
    | CustomFilter<string, Array<string>>
    | undefined

  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState(resolvedFilter?.value)

  useEffect(() => {
    setValue(resolvedFilter?.value)
  }, [resolvedFilter])

  const handleApply = () => {
    if (!value) {
      clearColumnFilter()
      setIsOpen(false)
      return
    }
    const nextFilter: TableFilter<TrackingWalletsTableColumnId> = {
      type: 'custom',
      column: columnId,
      value,
      predicate: (cellValue) => {
        if (!Array.isArray(cellValue)) {
          return false
        }
        const tags = cellValue as Array<string>
        const commonTags = [...Iterable.intersection(tags)(value)]
        return commonTags.length > 0
      },
    }
    setColumnFilter(nextFilter)
    setIsOpen(false)
  }

  const handleReset = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    clearColumnFilter()
    setValue([])
    setIsOpen(false)
  }
  const hasFilter = !!resolvedFilter

  const trigger = (
    <FilterByMakerTrigger>
      <FilterButton active={isOpen || hasFilter} />
      {t('wallet-tracker.table.tags')}
    </FilterByMakerTrigger>
  )

  return (
    <FilterPopover
      trigger={trigger}
      title={t('wallet-tracker.filter.tags.name')}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <FilterByMakerContent
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          handleApply()
        }}
      >
        <Input
          size="m"
          placeholder={t('wallet-tracker.filter.tags.placeholder')}
          value={value?.join(',')}
          onChange={(e) =>
            setValue(e.target.value.split(',').map((x) => x.trim()))
          }
          css={css`
            --input-height: 37px;
          `}
        />
        <FilterByMakerFooter>
          <FilterResetButton
            variant="ghost"
            radius="rounded"
            size="xs"
            onClick={handleReset}
            disabled={!value}
          >
            <ResetIcon />
            {t('trade.filters.common.reset')}
          </FilterResetButton>
          <Button
            variant="solid"
            color="primary"
            radius="rounded"
            size="xs"
            onClick={handleApply}
            disabled={!value}
          >
            {t('trade.filters.common.apply')}
          </Button>
        </FilterByMakerFooter>
      </FilterByMakerContent>
    </FilterPopover>
  )
}

const FilterByMakerTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  color: var(--text-11);

  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(--text-12);
  }
`

const FilterByMakerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FilterByMakerContent = styled.form`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 6px;
`

const FilterResetButton = styled(Button)`
  gap: 4px;
  padding-left: 0px;
  font-weight: 400;
  &:hover {
    background: transparent;
  }
`
